// ============================================================================
// USER SEARCH HOOKS
// ============================================================================
//
// PURPOSE: Search and paginate app users
//
// PROVIDES:
// - useSearchUsersGraphQL  — GraphQL ILIKE-based user search with pagination
// - useFetchMoreUsers      — Cursor-based pagination helper
// - useSearchEmployeesFTS  — Full-text search via /api/search/employees
//
// ============================================================================

import { useMemo, useCallback, useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
  SearchUsersDocument,
  type SearchUsersQuery,
  type SearchUsersQueryVariables,
} from "@/src/gql/graphql";
import { useUniversalAuth } from "@wtc/ui";
import { logger } from "@/lib/logger";

/** One `app_users` node exactly as the SearchUsers operation selects it. */
export type SearchedUser = SearchUsersQuery["app_usersCollection"]["edges"][number]["node"];

/**
 * Apollo's `fetchMore`, described structurally: `useFetchMoreUsers` is handed the
 * `fetchMore` returned by `useSearchUsersGraphQL` and only ever calls it with
 * these options.
 */
type SearchUsersFetchMore = (options: {
  variables: Partial<SearchUsersQueryVariables>;
  updateQuery: (
    previous: SearchUsersQuery,
    options: { fetchMoreResult: SearchUsersQuery }
  ) => SearchUsersQuery;
}) => Promise<unknown>;

/**
 * A row from `POST /api/search/employees`. Only the fields the select
 * components read are modelled; the endpoint returns the full employee row.
 */
export interface EmployeeSearchResult {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_active?: boolean | null;
  is_billable?: boolean | null;
  available_hours?: number | null;
  hourly_rate?: number | null;
}

function isEmployeeSearchResult(value: unknown): value is EmployeeSearchResult {
  if (!value || typeof value !== "object") return false;
  const row = value as { id?: unknown; email?: unknown };
  return typeof row.id === "string" && typeof row.email === "string";
}

/**
 * Search users with pagination and search filtering.
 * Used by UserSelect and MultiUserSelect components.
 */
export function useSearchUsersGraphQL(
  searchTerm: string = "",
  pageSize: number = 10,
  isActive: boolean = true
) {
  const { isAuthenticated } = useUniversalAuth();

  // Build search filter - if no search term, use wildcard to match all
  const searchFilter = searchTerm ? `%${searchTerm}%` : "%";

  const result = useQuery<SearchUsersQuery, SearchUsersQueryVariables>(SearchUsersDocument, {
    variables: {
      first: pageSize,
      after: null,
      search: searchFilter,
      isActive: isActive,
    },
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated,
  });

  return useMemo(() => {
    const users =
      result.data?.app_usersCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is SearchedUser => node != null) || [];

    const pageInfo = result.data?.app_usersCollection?.pageInfo || {
      hasNextPage: false,
      endCursor: null,
    };

    return {
      users,
      pageInfo,
      isLoading: result.loading,
      error: result.error,
      fetchMore: result.fetchMore,
      refetch: result.refetch,
    };
  }, [result.data, result.loading, result.error, result.fetchMore, result.refetch]);
}

/**
 * Fetch next page of users (cursor-based pagination helper).
 */
export function useFetchMoreUsers() {
  return useCallback(
    async (
      fetchMore: SearchUsersFetchMore,
      // `pageInfo.endCursor` is optional in codegen; the falsy guard below covers it.
      endCursor: string | null | undefined,
      searchTerm: string = "",
      pageSize: number = 10,
      isActive: boolean = true
    ) => {
      if (!endCursor) return;

      const searchFilter = searchTerm ? `%${searchTerm}%` : "%";

      try {
        await fetchMore({
          variables: {
            after: endCursor,
            first: pageSize,
            search: searchFilter,
            isActive: isActive,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              app_usersCollection: {
                ...fetchMoreResult.app_usersCollection,
                edges: [
                  ...(prev.app_usersCollection?.edges || []),
                  ...(fetchMoreResult.app_usersCollection?.edges || []),
                ],
              },
            };
          },
        });
      } catch (error) {
        logger.error("Error fetching more users", error);
      }
    },
    []
  );
}

/**
 * FTS search for employees using the /api/search/employees endpoint.
 * Provides better search quality than GraphQL ILIKE queries.
 */
export function useSearchEmployeesFTS(
  searchTerm: string = "",
  pageSize: number = 30,
  isActive: boolean = true
) {
  const { isAuthenticated } = useUniversalAuth();
  const [users, setUsers] = useState<EmployeeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const performSearch = useCallback(async () => {
    if (!searchTerm || !isAuthenticated) {
      setUsers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/search/employees", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: searchTerm, limit: pageSize, offset: 0 }),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const payload: unknown = await response.json();
      const rows =
        payload && typeof payload === "object" && "success" in payload && payload.success
          ? (payload as { data?: unknown }).data
          : undefined;

      if (Array.isArray(rows)) {
        const parsed = rows.filter(isEmployeeSearchResult);
        setUsers(isActive ? parsed.filter((u) => u.is_active !== false) : parsed);
      } else {
        setUsers([]);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      setError(errorObj);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, pageSize, isActive, isAuthenticated]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void performSearch();
    }, 300); // Debounce search

    return () => clearTimeout(timer);
  }, [performSearch]);

  return useMemo(
    () => ({ users, isLoading, error }),
    [users, isLoading, error]
  );
}
